import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Copy, 
  Sparkles, 
  Heart, 
  Trash2, 
  RefreshCw,
  BookmarkPlus,
  BookMarked,
  CheckCircle,
  Hash
} from 'lucide-react';


const AICaptionGenerator = () => {
  const [contentType, setContentType] = useState('studio_vibe');
  const [engineer, setEngineer] = useState('Trap Spade');
  const [captionCount, setCaptionCount] = useState(10);
  const [captions, setCaptions] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedCaptions, setSavedCaptions] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const contentTypes = [
    { value: 'studio_vibe', label: '🎤 Studio Vibe', description: 'Studio atmosphere and energy' },
    { value: 'engineer_spotlight', label: '🎚️ Engineer Spotlight', description: 'Highlight your engineers' },
    { value: 'last_minute_deal', label: '🚨 Last Minute Deal', description: 'Urgent booking promotions' },
    { value: 'artist_testimonial', label: '🎵 Artist Testimonial', description: 'Client success stories' },
    { value: 'tips_value', label: '💡 Studio Tips', description: 'Recording tips and advice' },
    { value: 'session_vibe', label: '📸 Session BTS', description: 'Behind the scenes content' },
    { value: 'before_after', label: '🎧 Before/After', description: 'Mix transformation showcase' },
    { value: 'motivational', label: '💪 Motivational', description: 'Inspire your audience' },
    { value: 'new_music', label: '🚀 New Music', description: 'Tease upcoming releases' },
    { value: 'booking_cta', label: '📅 Book Now', description: 'Direct booking calls to action' }
  ];

  const engineers = ['Trap Spade', 'Chapo', 'Dev', 'Shotz'];

  useEffect(() => {
    fetchSavedCaptions();
  }, []);

  const fetchSavedCaptions = async () => {
    try {
      const response = await axios.get(`/api/marketing/captions/saved`);
      setSavedCaptions(response.data.captions || []);
    } catch (error) {
      console.error('Error fetching saved captions:', error);
    }
  };

  const generateCaptions = async () => {
    setLoading(true);
    setCaptions([]);
    try {
      const params = new URLSearchParams({
        content_type: contentType,
        count: captionCount.toString()
      });
      
      if (contentType === 'engineer_spotlight') {
        params.append('engineer', engineer);
      }
      
      const response = await axios.get(
        `/api/marketing/captions/generate?${params}`
      );
      
      setCaptions(response.data.captions || []);
      setHashtags(response.data.hashtags || []);
      toast.success(`Generated ${response.data.captions?.length || 0} captions!`);
    } catch (error) {
      console.error('Error generating captions:', error);
      toast.error('Failed to generate captions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const copyHashtags = async () => {
    try {
      await navigator.clipboard.writeText(hashtags.join(' '));
      toast.success('Hashtags copied!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const saveCaption = async (caption) => {
    try {
      await axios.post(`/api/marketing/captions/save`, {
        caption,
        content_type: contentType
      });
      toast.success('Caption saved!');
      fetchSavedCaptions();
    } catch (error) {
      console.error('Error saving caption:', error);
      toast.error('Failed to save caption');
    }
  };

  const deleteSavedCaption = async (captionId) => {
    try {
      await axios.delete(`/api/marketing/captions/saved/${captionId}`);
      toast.success('Caption deleted');
      fetchSavedCaptions();
    } catch (error) {
      console.error('Error deleting caption:', error);
      toast.error('Failed to delete caption');
    }
  };

  const isCaptionSaved = (caption) => {
    return savedCaptions.some(sc => sc.caption === caption);
  };

  return (
    <div className="space-y-6">
      {/* Header with Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">AI Caption Generator</h2>
          <p className="text-gray-400 text-sm">Generate unique Instagram & TikTok captions with AI</p>
        </div>
        <button
          onClick={() => setShowSaved(!showSaved)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
            showSaved 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          <BookMarked size={18} />
          Saved ({savedCaptions.length})
        </button>
      </div>

      {/* Saved Captions View */}
      {showSaved ? (
        <div className="space-y-4">
          <div className="bg-gray-900/50 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Heart className="text-red-400" size={24} />
              Saved Captions
            </h3>
            
            {savedCaptions.length === 0 ? (
              <div className="text-center py-12">
                <BookmarkPlus className="mx-auto text-gray-600 mb-4" size={48} />
                <p className="text-gray-400">No saved captions yet</p>
                <p className="text-gray-500 text-sm">Generate captions and save your favorites!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedCaptions.map((saved, idx) => (
                  <div 
                    key={saved.id || idx}
                    className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <p className="text-white whitespace-pre-wrap">{saved.caption}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span className="bg-gray-700 px-2 py-1 rounded">
                            {saved.content_type?.replace('_', ' ')}
                          </span>
                          {saved.used_count > 0 && (
                            <span>Used {saved.used_count}x</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(saved.caption, `saved-${idx}`)}
                          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                          title="Copy"
                        >
                          {copiedIndex === `saved-${idx}` ? (
                            <CheckCircle size={18} className="text-green-400" />
                          ) : (
                            <Copy size={18} className="text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteSavedCaption(saved.id)}
                          className="p-2 bg-red-900/30 hover:bg-red-900/50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Generator Controls */}
          <div className="bg-gray-900/50 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Content Type */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Content Type
                </label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
                >
                  {contentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Engineer (conditional) */}
              {contentType === 'engineer_spotlight' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Engineer
                  </label>
                  <select
                    value={engineer}
                    onChange={(e) => setEngineer(e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
                  >
                    {engineers.map((eng) => (
                      <option key={eng} value={eng}>{eng}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Caption Count */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Captions
                </label>
                <select
                  value={captionCount}
                  onChange={(e) => setCaptionCount(parseInt(e.target.value))}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
                >
                  <option value={5}>5 captions</option>
                  <option value={10}>10 captions</option>
                  <option value={15}>15 captions</option>
                </select>
              </div>

              {/* Generate Button */}
              <div className="flex items-end">
                <button
                  onClick={generateCaptions}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw size={20} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Generated Captions */}
          {captions.length > 0 && (
            <div className="space-y-4">
              {/* Hashtags Section */}
              <div className="bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Hash size={20} className="text-blue-400" />
                    Hashtags
                  </h3>
                  <button
                    onClick={copyHashtags}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
                  >
                    <Copy size={16} />
                    Copy All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {hashtags.map((tag, idx) => (
                    <span 
                      key={idx}
                      className="bg-blue-900/30 text-blue-400 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Captions Grid */}
              <div className="bg-gray-900/50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  Generated Captions ({captions.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {captions.map((caption, idx) => (
                    <div 
                      key={idx}
                      className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-purple-500/50 transition-colors"
                    >
                      <p className="text-white whitespace-pre-wrap mb-4 text-sm leading-relaxed">
                        {caption}
                      </p>
                      <div className="flex justify-between items-center pt-3 border-t border-gray-700">
                        <span className="text-gray-500 text-xs">#{idx + 1}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => copyToClipboard(caption, idx)}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Copy caption"
                          >
                            {copiedIndex === idx ? (
                              <CheckCircle size={18} className="text-green-400" />
                            ) : (
                              <Copy size={18} className="text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => saveCaption(caption)}
                            disabled={isCaptionSaved(caption)}
                            className={`p-2 rounded-lg transition-colors ${
                              isCaptionSaved(caption)
                                ? 'bg-red-900/30 cursor-not-allowed'
                                : 'bg-gray-700 hover:bg-red-900/50'
                            }`}
                            title={isCaptionSaved(caption) ? 'Already saved' : 'Save caption'}
                          >
                            <Heart 
                              size={18} 
                              className={isCaptionSaved(caption) ? 'text-red-400 fill-red-400' : 'text-gray-400'}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {captions.length === 0 && !loading && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
              <h4 className="text-blue-300 font-semibold mb-3">How to Use</h4>
              <ul className="text-blue-400 text-sm space-y-2">
                <li>1. Select a content type that matches what you're posting</li>
                <li>2. Choose how many captions you want (5-15)</li>
                <li>3. Click "Generate" to create unique AI-powered captions</li>
                <li>4. Copy captions you like or save them for later</li>
                <li>5. Use the hashtags section for maximum reach</li>
              </ul>
              <div className="mt-4 p-3 bg-purple-900/30 rounded-lg">
                <p className="text-purple-300 text-sm">
                  💡 <strong>Pro Tip:</strong> Each generation creates completely new captions. 
                  If you don't love what you see, just generate again!
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AICaptionGenerator;
